import React, { useEffect } from "react"
import { Col, Container, Row } from "reactstrap"
import PhotoSlider from "components/common/PhotoSlider"
import TripsCalendar from "components/guides/Calendar"
import Trip from "components/trips/Trip"

import { useTitle } from "hooks/useTitle"
import { useTranslation } from "react-i18next"

import { modelSelector } from "store/selectors"
import { useDispatch, useSelector } from "react-redux"
import { cleanTrips } from "store/trips"
import { isGuideSelector } from "store/user"
import { guideSelector } from "store/user"
import { MediaPreview } from "ui/MediaPreview"
import { VideoPlayerModal } from "ui/VideoPlayerModal"
import { isMediaFileVideo } from "utils/media-upload"
import { PreviewGallery } from "ui/PersonalInfo/ProfilePreview/PreviewGallery"

function renderPhotoGrid(photos, handleMediaClick, guideName) {
  if (photos.length === 0) return null

  if (photos.length === 1)
    return (
      <div className="grid gap-2 mt-15">
        <div className="g-col-12 g-col-lg-12">
          <MediaPreview
            media={photos.at(0)}
            src={photos.at(0).url}
            alt=""
            className="object-fit-cover w-100 h-100 rounded"
            wrapperClassName="w-100 h-100"
            effect="blur"
            placeholderSrc={photos.at(0).placeholder_url || null}
            onClick={() => handleMediaClick?.(photos.at(0))}
          />
        </div>
      </div>
    )

  if (photos.length === 2)
    return (
      <div className="grid gap-2 mt-15">
        <div className="g-col-12 g-col-md-12 g-col-lg-6" style={{ maxHeight: 420 }}>
          <MediaPreview
            media={photos.at(0)}
            src={photos.at(0).url}
            alt=""
            className="object-fit-cover w-100 h-100 rounded"
            wrapperClassName="w-100 h-100"
            effect="blur"
            placeholderSrc={photos.at(0).placeholder_url || null}
            onClick={() => handleMediaClick?.(photos.at(0))}
          />
        </div>
        <div className="g-col-12 g-col-md-12 g-col-lg-6" style={{ maxHeight: 420 }}>
          <MediaPreview
            media={photos.at(1)}
            src={photos.at(1).url}
            alt=""
            className="object-fit-cover w-100 h-100 rounded"
            wrapperClassName="w-100 h-100"
            effect="blur"
            placeholderSrc={photos.at(1).placeholder_url || null}
            onClick={() => handleMediaClick?.(photos.at(0))}
          />
        </div>
      </div>
    )

  if (photos.length === 3)
    return (
      <div className="grid gap-2 mt-15">
        <div className="g-col-12 g-col-lg-6" style={{ maxHeight: 560 }}>
          <MediaPreview
            media={photos.at(0)}
            src={photos.at(0).url}
            alt=""
            className="object-fit-cover w-100 h-100 rounded"
            wrapperClassName="w-100 h-100"
            effect="blur"
            placeholderSrc={photos.at(0).placeholder_url || null}
            onClick={() => handleMediaClick?.(photos.at(0))}
          />
        </div>
        <div className="g-col-12 g-col-lg-6 grid grid-cols-6 gap-2">
          <div className="g-col-6 grid grid-cols-1 grid-rows-8 gap-2" style={{ maxHeight: 560 }}>
            <div className="g-row-4">
              <MediaPreview
                media={photos.at(1)}
                src={photos.at(1).url}
                alt=""
                className="object-fit-cover w-100 h-100 rounded"
                wrapperClassName="w-100 h-100"
                effect="blur"
                placeholderSrc={photos.at(1).placeholder_url || null}
                onClick={() => handleMediaClick?.(photos.at(1))}
              />
            </div>
            <div className="g-row-4">
              <MediaPreview
                media={photos.at(2)}
                src={photos.at(2).url}
                alt=""
                className="object-fit-cover w-100 h-100 rounded"
                wrapperClassName="w-100 h-100"
                effect="blur"
                placeholderSrc={photos.at(2).placeholder_url || null}
                onClick={() => handleMediaClick?.(photos.at(2))}
              />
            </div>
          </div>
        </div>
      </div>
    )

  if (photos.length > 3)
    return (
      <div className="grid gap-2 mt-15">
        <div className="g-col-12 g-col-lg-6" style={{ maxHeight: 560 }}>
          <MediaPreview
            media={photos.at(0)}
            src={photos.at(0).url}
            alt=""
            className="object-fit-cover w-100 h-100 rounded"
            wrapperClassName="w-100 h-100"
            effect="blur"
            placeholderSrc={photos.at(0).placeholder_url || null}
            onClick={() => handleMediaClick?.(photos.at(0))}
          />
        </div>
        <div className="g-col-12 g-col-lg-6 grid grid-cols-6 gap-2">
          <div className="g-col-6 grid grid-cols-1 grid-rows-8 gap-2" style={{ maxHeight: 560 }}>
            <div className="g-row-4">
              <MediaPreview
                media={photos.at(1)}
                src={photos.at(1).url}
                alt=""
                className="object-fit-cover w-100 h-100 rounded"
                wrapperClassName="w-100 h-100"
                effect="blur"
                placeholderSrc={photos.at(1).placeholder_url || null}
                onClick={() => handleMediaClick?.(photos.at(1))}
              />
            </div>
            <div className="g-row-4 position-relative">
              <div className="position-absolute bottom-0 end-0 p-3 z-2">
                <PreviewGallery name={guideName} mediaItems={photos} hideGrid />
              </div>
              <MediaPreview
                media={photos.at(2)}
                src={photos.at(2).url}
                alt=""
                className="object-fit-cover w-100 h-100 rounded"
                wrapperClassName="w-100 h-100"
                effect="blur"
                placeholderSrc={photos.at(2).placeholder_url || null}
                onClick={() => handleMediaClick?.(photos.at(2))}
              />
            </div>
          </div>
        </div>
      </div>
    )
}

export default function Guide() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isGuide = useSelector(isGuideSelector)
  const guide = useSelector(guideSelector) || {}
  const tripGuide = useSelector(modelSelector("guide"))
  const { description, expertises, popular_trips } = guide
  useTitle(guide.display_name)

  const media = isGuide
    ? [...guide.photos, ...(guide.videos || [])].sort((a, b) => a.position - b.position)
    : [...tripGuide.photos, ...(tripGuide.videos || [])].sort((a, b) => a.position - b.position)

  const hasPopularTrips = !!popular_trips?.length
  const [videoModalOpen, setVideoModalOpen] = React.useState(false)
  const [selectedMedia, setSelectedMedia] = React.useState(null)

  const handleMediaClick = (media) => {
    if (isMediaFileVideo(media)) {
      setSelectedMedia(media)
      setVideoModalOpen(true)
    }
  }

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false)
    setSelectedMedia(null)
  }

  useEffect(() => {
    return () => dispatch(cleanTrips())
  })

  return (
    <Container className="mb-n40">
      {media && media.length > 0 && (
        <>
          <div className="d-none d-lg-block">
            {renderPhotoGrid(media, handleMediaClick, tripGuide?.display_name || guide?.display_name)}
          </div>
          <div className="d-block d-lg-none">
            <PhotoSlider photos={guide.photos} ratio="16x9" className="w-100 h-100 mt-20" />
          </div>
        </>
      )}
      {description && (
        <section className="my-60 text-prewrap">
          <h2 className="fw-semibold mb-30">{t("global.about")}</h2>
          {description}
        </section>
      )}
      {expertises?.length > 0 && (
        <section className="my-80">
          <h2 className="fw-semibold mb-30">{t("guide.labels.experience_expertise")}</h2>
          <ul className="grid grid-cols-1 grid-cols-sm-2 grid-cols-md-3 grid-cols-lg-4 gap-15 list-unstyled m-0 mt-15">
            {expertises?.length ? expertises.map((item) => <li key={item.id}>— {item.name}</li>) : "—"}
          </ul>
        </section>
      )}
      <section className="my-80">
        <h2 className="fw-semibold mb-30">{t("guide.labels.calendar", { guide_name: guide.display_name })}</h2>
        <TripsCalendar />
      </section>
      {hasPopularTrips ? (
        <section className="my-80">
          <h2 className="fw-semibold mb-30">{t("guides.popular_trips")}</h2>
          <Row className="gap-y-20 gx-25 gx-xxl-90 mt-35">
            {popular_trips.map((trip) => (
              <Col xs={12} lg={4} key={trip.id}>
                <Trip trip={trip} className="h-100" guideView={isGuide} />
              </Col>
            ))}
          </Row>
        </section>
      ) : null}
      <VideoPlayerModal open={videoModalOpen} handleClose={handleCloseVideoModal} selectedMedia={selectedMedia} />
    </Container>
  )
}
